class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
    this.autoPages = parseInt(this.dataset.autoPages, 10) || 3;
    this.buttonPages = parseInt(this.dataset.buttonPages, 10) || 2;
    this.sectionId = this.dataset.sectionId;
    this.loading = false;
    this.initialized = false;
    this.mode = 'auto'; // 'auto' | 'button' | 'hidden'
    this.observer = null;
    this.sentinel = null;
    this.loadedPages = new Set(); // prevent duplicate page loads

    this.onButtonClick = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.buttonContainer = this.querySelector('.load-more-button__container');
    this.button = this.querySelector('.load-more-button');
    this.grid = document.getElementById('product-grid');

    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick);
    }

    this.loadedPages.add(this.currentPage);

    if (this.currentPage >= this.totalPages) {
      this.hide();
      return;
    }

    if (this.currentPage < this.autoPages) {
      this.mode = 'auto';
      this.insertSentinel();
      this.checkAutoLoad();
    } else {
      this.mode = 'button';
      this.showButton();
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClick);
    }
    this.destroyObserver();
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.parentNode.removeChild(this.sentinel);
      this.sentinel = null;
    }
  }

  /* ---- Sentinel ---- */

  insertSentinel() {
    if (!this.grid) return;
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.parentNode.removeChild(this.sentinel);
    }
    this.sentinel = document.createElement('li');
    this.sentinel.className = 'load-more-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.sentinel.style.cssText = 'height:1px;width:100%;pointer-events:none;';
    this.grid.appendChild(this.sentinel);
    this.startObserver();
  }

  moveSentinelToEnd() {
    if (!this.grid || !this.sentinel) return;
    this.grid.appendChild(this.sentinel);
  }

  startObserver() {
    this.destroyObserver();
    if (!this.sentinel) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loading && this.mode === 'auto') {
            this.loadNextPage();
          }
        });
      },
      { rootMargin: '0px 0px 2000px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /* ---- Proactive check ---- */

  checkAutoLoad() {
    if (this.loading || this.mode !== 'auto') return;
    if (this.currentPage >= this.autoPages || this.currentPage >= this.totalPages) return;
    if (!this.sentinel) return;

    const rect = this.sentinel.getBoundingClientRect();
    if (rect.top < window.innerHeight + 2000) {
      this.loadNextPage();
    }
  }

  /* ---- Button ---- */

  onButtonClick() {
    if (this.loading) return;
    this.loadPages(this.buttonPages);
  }

  /* ---- Core ---- */

  async loadPages(pageCount) {
    const pagesToLoad = parseInt(pageCount, 10) || 1;

    for (let i = 0; i < pagesToLoad; i += 1) {
      if (this.currentPage >= this.totalPages || this.mode === 'hidden') break;

      const loaded = await this.loadNextPage();
      if (!loaded) break;
    }
  }

  async loadNextPage() {
    if (this.loading) return false;

    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) {
      this.hide();
      return false;
    }
    if (this.loadedPages.has(nextPage)) return false;

    this.loading = true;
    this.loadedPages.add(nextPage);
    this.showLoading();

    try {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('page');
      searchParams.set('page', nextPage.toString());
      const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams.toString()}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const productGrid = html.getElementById('product-grid');
      if (!productGrid) {
        this.hide();
        return false;
      }

      const newItems = [];
      productGrid.childNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'LI') {
          newItems.push(node);
        }
      });

      if (newItems.length === 0) {
        this.hide();
        return false;
      }

      newItems.forEach((item) => {
        this.grid.appendChild(item);
      });
      this.reinitializeProductCards(newItems);

      this.currentPage = nextPage;
      this.dataset.currentPage = nextPage.toString();
      this.moveSentinelToEnd();

      if (this.currentPage >= this.totalPages) {
        this.hide();
      } else if (this.currentPage >= this.autoPages) {
        this.switchToButtonMode();
      } else {
        requestAnimationFrame(() => this.checkAutoLoad());
      }

      return true;
    } catch (error) {
      this.loadedPages.delete(nextPage);
      return false;
    } finally {
      this.hideLoading();
      this.loading = false;
    }
  }

  reinitializeProductCards(items) {
    if (!items.length || typeof window.setupCardColorSwatches !== 'function') return;

    const run = () => {
      items.forEach((item) => {
        window.setupCardColorSwatches(item);
      });
    };

    requestAnimationFrame(run);
    setTimeout(run, 250);
  }

  /* ---- UI state ---- */

  showLoading() {
    if (this.button) this.button.classList.add('loading');
  }

  hideLoading() {
    if (this.button) this.button.classList.remove('loading');
  }

  switchToButtonMode() {
    this.mode = 'button';
    this.destroyObserver();
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.parentNode.removeChild(this.sentinel);
      this.sentinel = null;
    }
    this.showButton();
  }

  showButton() {
    if (this.buttonContainer) this.buttonContainer.classList.remove('hidden');
  }

  hide() {
    this.mode = 'hidden';
    this.destroyObserver();
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.parentNode.removeChild(this.sentinel);
      this.sentinel = null;
    }
    if (this.buttonContainer) this.buttonContainer.classList.add('hidden');
  }
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}
